import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, MessageCircle, Phone, Send, Twitter, Youtube } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function Footer({ site }) {
  const phone = site?.phone || "0341-9300560";
  const whatsapp = site?.whatsapp || "923419300560";
  const email = site?.email || "contact@afzalmedicalcomplex.com";
  const address = site?.address || "Main GT Road, Afzal Medical Complex";

  const [nEmail, setNEmail] = useState("");
  const [nBusy, setNBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nEmail) return;
    setNBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: nEmail });
      toast.success(data.already_subscribed ? "You're already subscribed 🙂" : "Thanks — welcome aboard!");
      setNEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNBusy(false);
    }
  };

  return (
    <footer className="bg-slate-950 text-slate-300 mt-24" data-testid="site-footer">
      {/* Newsletter strip */}
      <div className="border-b border-slate-800 bg-gradient-to-r from-slate-950 via-red-950/40 to-slate-950">
        <div className="container-medi py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-red-400 font-semibold mb-2">Health Tips Newsletter</div>
            <h3 className="text-2xl lg:text-3xl font-heading font-semibold text-white">Get evidence-based health advice in your inbox.</h3>
            <p className="text-sm text-slate-400 mt-1">Occasional emails — no spam, unsubscribe anytime.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={nEmail}
              onChange={(e) => setNEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 rounded-full bg-slate-900 border border-slate-700 text-white placeholder:text-slate-500 px-5 py-3 text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
              data-testid="newsletter-email"
            />
            <button type="submit" disabled={nBusy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-medium disabled:opacity-60" data-testid="newsletter-submit">
              <Send className="w-4 h-4" /> {nBusy ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="container-medi py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/amc-logo.png" alt="AMC" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-heading font-semibold text-white text-base">Afzal Medical</div>
              <div className="text-[11px] tracking-widest uppercase text-red-400">Complex</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-slate-400 max-w-xs">
            Compassionate care and modern diagnostics under one roof — proudly serving our community 24/7.
          </p>
          <div className="flex gap-3 mt-5">
            {[
              { href: site?.facebook_url || "#", icon: Facebook, name: "Facebook" },
              { href: `https://wa.me/${whatsapp}`, icon: MessageCircle, name: "WhatsApp" },
              { href: site?.instagram_url || "#", icon: Instagram, name: "Instagram" },
              { href: site?.twitter_url || "#", icon: Twitter, name: "Twitter" },
              { href: site?.youtube_url || "#", icon: Youtube, name: "Youtube" },
            ].filter(s => s.href && s.href !== "#").map(({ href, icon: Icon, name }) => (
              <a key={name} href={href} target="_blank" rel="noreferrer"
                 className="w-9 h-9 rounded-full bg-slate-800 hover:bg-red-700 grid place-items-center transition"
                 data-testid={`social-${name.toLowerCase()}`} aria-label={name}>
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="text-white font-heading font-medium mb-4">Explore</div>
          <ul className="space-y-2.5 text-sm">
            {[
              ["/", "Home"],
              ["/about", "About"],
              ["/services", "Services"],
              ["/departments", "Departments"],
              ["/doctors", "Doctors"],
              ["/blog", "Health Tips"],
              ["/contact", "Contact"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link to={to} className="hover:text-red-400 transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g,'-')}`}>{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-white font-heading font-medium mb-4">Services</div>
          <ul className="space-y-2.5 text-sm text-slate-400">
            {[
              ["/departments/radiology", "Radiology & Imaging"],
              ["/departments/pathology", "Pathology Lab"],
              ["/departments/endoscopy", "Endoscopy"],
              ["/departments/dentistry", "Dentistry"],
              ["/departments/physiotherapy", "Physiotherapy"],
              ["/departments/pharmacy", "Pharmacy 24/7"],
              ["/departments/surgery", "Surgery"],
            ].map(([to, label]) => (
              <li key={to}><Link to={to} className="hover:text-red-400 transition-colors" data-testid={`footer-service-${label.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}`}>{label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-white font-heading font-medium mb-4">Get in Touch</div>
          <ul className="space-y-3 text-sm text-slate-400">
            <li className="flex gap-2.5"><MapPin className="w-4 h-4 mt-0.5 text-red-400 flex-shrink-0" /> <span>{address}</span></li>
            <li className="flex gap-2.5"><Phone className="w-4 h-4 mt-0.5 text-red-400 flex-shrink-0" /> <a href={`tel:${phone}`} className="hover:text-red-400">{phone}</a></li>
            <li className="flex gap-2.5"><MessageCircle className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" /> <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-emerald-400">WhatsApp {phone}</a></li>
            <li className="flex gap-2.5"><Mail className="w-4 h-4 mt-0.5 text-red-400 flex-shrink-0" /> <a href={`mailto:${email}`} className="hover:text-red-400 break-all">{email}</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="container-medi py-5 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-3">
          <span>© {new Date().getFullYear()} Afzal Medical Complex. All rights reserved.</span>
          <Link to="/admin/login" className="hover:text-red-400" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
